import { z } from 'zod';
import { DEFAULT_BUSINESS_TZ, instantFromWall, wallMs } from './business-hours';
import { Dateish } from './common';

// ── Atividades agendadas (v5, módulo scheduled) ─────────────────────────────

export const RecurrenceKindSchema = z.enum(['daily', 'weekly', 'monthly']);
export type RecurrenceKind = z.infer<typeof RecurrenceKindSchema>;

export const ScheduledActivitySchema = z.object({
  id: z.number().int(),
  deskId: z.number().int(),
  name: z.string(),
  active: z.boolean(),
  ticketTitle: z.string(),
  ticketDescription: z.string().nullable(),
  clientId: z.number().int(),
  priorityId: z.number().int().nullable(),
  catalogItemId: z.number().int().nullable(),
  responsibleId: z.number().int().nullable(),
  recurrence: RecurrenceKindSchema,
  /** 0 = domingo … 6 = sábado; não-nulo quando recurrence = weekly */
  weekday: z.number().int().min(0).max(6).nullable(),
  /** 1..28; não-nulo quando recurrence = monthly */
  monthDay: z.number().int().min(1).max(28).nullable(),
  /** minutos desde a meia-noite LOCAL (0–1439, America/Sao_Paulo) */
  timeMinute: z.number().int().min(0).max(1439),
  nextRunAt: Dateish,
  lastRunAt: Dateish.nullable(),
  createdAt: Dateish,
});
export type ScheduledActivity = z.infer<typeof ScheduledActivitySchema>;

/** Item de GET /scheduled-activities — lista do tenant inteiro com o nome da mesa. */
export const ScheduledActivityListItemSchema = ScheduledActivitySchema.extend({
  deskName: z.string(),
});
export type ScheduledActivityListItem = z.infer<typeof ScheduledActivityListItemSchema>;

/** GET /scheduled-activities?desk_id — filtro opcional por mesa. */
export const ScheduledActivityListQuerySchema = z.object({
  desk_id: z.coerce.number().int().positive().optional(),
});
export type ScheduledActivityListQuery = z.infer<typeof ScheduledActivityListQuerySchema>;

const scheduledRecurrenceRule = (
  b: { recurrence: RecurrenceKind; weekday?: number | null; monthDay?: number | null },
  ctx: z.RefinementCtx
): void => {
  if (b.recurrence === 'weekly' && b.weekday == null) {
    ctx.addIssue({
      code: 'custom',
      path: ['weekday'],
      message: 'weekday is required when recurrence is weekly',
    });
  }
  if (b.recurrence === 'monthly' && b.monthDay == null) {
    ctx.addIssue({
      code: 'custom',
      path: ['monthDay'],
      message: 'monthDay is required when recurrence is monthly',
    });
  }
};

/** POST/PUT — weekly exige weekday, monthly exige monthDay (1..28). */
const ScheduledActivityBodyObjectSchema = z
  .object({
    name: z.string().min(1),
    active: z.boolean().optional(),
    ticketTitle: z.string().min(1),
    ticketDescription: z.string().nullish(),
    clientId: z.number().int().positive(),
    priorityId: z.number().int().positive().nullish(),
    catalogItemId: z.number().int().positive().nullish(),
    responsibleId: z.number().int().positive().nullish(),
    recurrence: RecurrenceKindSchema,
    weekday: z.number().int().min(0).max(6).nullish(),
    monthDay: z.number().int().min(1).max(28).nullish(),
    timeMinute: z.number().int().min(0).max(1439),
  });

export const ScheduledActivityBodySchema =
  ScheduledActivityBodyObjectSchema.superRefine(scheduledRecurrenceRule);
export type ScheduledActivityBody = z.infer<typeof ScheduledActivityBodySchema>;

/**
 * PUT /scheduled-activities/:id: deskId OPCIONAL — ausente mantém a mesa
 * atual; número MOVE a atividade (sem null: atividade agendada sempre tem
 * mesa). Alvos por-mesa (priorityId/catalogItemId) revalidam contra a mesa
 * nova no use-case.
 */
export const ScheduledActivityUpdateBodySchema = ScheduledActivityBodyObjectSchema.extend({
  deskId: z.number().int().positive().optional(),
}).superRefine(scheduledRecurrenceRule);
export type ScheduledActivityUpdateBody = z.infer<typeof ScheduledActivityUpdateBodySchema>;

// ── nextOccurrence ──────────────────────────────────────────────────────────

const DAY_MS = 86_400_000;
const MINUTE_MS = 60_000;

export interface RecurrenceSpec {
  recurrence: RecurrenceKind;
  /** 0 = domingo … 6 = sábado; obrigatório quando recurrence = weekly */
  weekday?: number | null;
  /** 1..28; obrigatório quando recurrence = monthly */
  monthDay?: number | null;
  /** minutos desde a meia-noite LOCAL (0–1439) */
  timeMinute: number;
}

/**
 * Primeiro disparo ESTRITAMENTE depois de `after` — PURA, mesma técnica de tz
 * do ./business-hours (wall clock "fake-UTC" via Intl). O "estritamente" é o
 * que impede o scheduler de disparar duas vezes no mesmo instante: avançar
 * next_run_at = nextOccurrence(now) logo após o disparo sempre cai no futuro.
 * monthDay é 1..28 de propósito: existe em todo mês, sem estouro de fevereiro.
 */
export function nextOccurrence(
  after: Date,
  rec: RecurrenceSpec,
  tz: string = DEFAULT_BUSINESS_TZ
): Date {
  const wall = wallMs(after.getTime(), tz);
  const dayStart = Math.floor(wall / DAY_MS) * DAY_MS;
  const tm = rec.timeMinute * MINUTE_MS;

  if (rec.recurrence === 'daily') {
    let cand = dayStart + tm;
    if (cand <= wall) cand += DAY_MS;
    return new Date(instantFromWall(cand, tz));
  }

  if (rec.recurrence === 'weekly') {
    if (rec.weekday == null) throw new Error('next_occurrence_weekday_required');
    // no espaço wall (fake-UTC) getUTCDay é o weekday LOCAL; d = 7 cobre
    // "hoje é o dia, mas o horário já passou" — mesma parede, semana que vem
    for (let d = 0; d <= 7; d++) {
      const ds = dayStart + d * DAY_MS;
      if (new Date(ds).getUTCDay() !== rec.weekday) continue;
      const cand = ds + tm;
      if (cand > wall) return new Date(instantFromWall(cand, tz));
    }
    throw new Error('next_occurrence_unreachable');
  }

  // monthly
  if (rec.monthDay == null) throw new Error('next_occurrence_month_day_required');
  const day = new Date(dayStart);
  const year = day.getUTCFullYear();
  const month = day.getUTCMonth();
  let cand = Date.UTC(year, month, rec.monthDay) + tm;
  // mês seguinte via Date.UTC(y, m+1, …): dezembro vira janeiro do ano seguinte
  if (cand <= wall) cand = Date.UTC(year, month + 1, rec.monthDay) + tm;
  return new Date(instantFromWall(cand, tz));
}
